
import { Vue, Component, Emit, Watch } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import vSelect3 from "vselect3";
import { UserPermissions, CancellableQuery, PaginatedSearchQueryParams } from "@/store/types";
import GenericTable, { TableHeader } from "./table/generic-table.vue";
import { OpsLink } from './OpsLinkCameraImport.vue';
import api from "@/services/api.service";
import { debounce } from 'lodash';
import { getDebouncePeriod } from '@/utils/debounce-helper';
import { ServerDetails, FailedOverServer } from '@/store/devices/types';
import { CancelTokenSource } from 'axios';
import { getNewCancelTokenSource } from '@/utils/cancellableQueryHelper';
import SelectedAppliance from '@/types/sv-data/appliances/SelectedAppliance';

interface OpsLinkExtended extends OpsLink {
	serverCount: number,
	secondaryApplianceId: number,
	affectedDevices: number
}

interface FailedOverServerRow extends FailedOverServer {
	isServerSelected: boolean,
}

const Devices = namespace("devices");

@Component({
	components: {
		"v-select-3": vSelect3,
		"generic-table": GenericTable,
	}
})
export default class OpsLinks extends Vue {
	@Getter getPermissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@Devices.State private failedOverDevicesList: FailedOverServer[];
	@Devices.State private totalFailedOverDevicesRows: number;
	@Devices.State private failOverCount: number;
	@Devices.State private totalOpsLinks: number;

	@Devices.Action private getFailedOverServersPaginated: (
		params: CancellableQuery<PaginatedSearchQueryParams>
	) => Promise<void>;
	@Devices.Action private getAppliancesPaginated: (
		params: CancellableQuery<PaginatedSearchQueryParams>
	) => Promise<SelectedAppliance[]>;
	@Devices.Action private loadFailOverCount: () => Promise<void>;
	@Devices.Action private moveFailedOverServersForAppliance: (applianceId?: number) => Promise<void>;
	@Devices.Action private moveSelectedServersBack: (serverIds: number[]) => Promise<void>;

	@Devices.Mutation private setFailedOverDevices: (ServerDetails[]);

	private configuredFailedOverDevices: FailedOverServerRow[] = [];
	private defaultResponseGrouping: string = "";
	private opsLinksList: OpsLinkExtended[] = [];
	private opsLinkModalShown: boolean = false;
	private confirmationMessageShown: boolean = false;
	private saveConfirmationMessageShown: boolean = false;
	private isMoveBackConfirmationShown: boolean = false;
	private responseMessage: string = "";
	private showResponseMessage: boolean = false;
	private selectedOpsLinkForReset: number = null;
	private loadSpinner: boolean = false;
	private tabIndex: number = 0;
	private pollAffectedDevicesBadgeTimeout: NodeJS.Timeout = null;

	// Ops Link Table
	private opsLinkCancelTokenSource: CancelTokenSource = null;
	private opsLinkTableLoading: boolean = false;
	private opsLinkPageNumber: number = 1;
	private opsLinkPageSize: number = 25;
	private opsLinkSortBy: string = "title";
	private opsLinkSortDesc: boolean = false;
	private opsLinkSearchTerm: string = "";


	// Affected Devices Table
	private affectedDevicesCancelTokenSource: CancelTokenSource = null;
	private affectedDevicesLoading: boolean = false;
	private affectedDevicesPageNumber: number = 1;
	private affectedDevicesPageSize: number = 25;
	private affectedDeviceSortBy: string = "title";
	private affectedDevicesSortDesc: boolean = false;
	private affectedDevicesSearchTerm: string = "";

	private defaultSearchParams: PaginatedSearchQueryParams = {
		page: 1,
		pageSize: 25,
	}

	private currentOpsLink: OpsLinkExtended = {
		id: null,
		groupId: null,
		title: null,
		isOnline: null,
		isLoading: null,
		devices: [],
		serverCount: null,
		secondaryApplianceId: null,
		affectedDevices: null
	};
	private secondaryOpsLink: OpsLinkExtended = {
		id: null,
		groupId: null,
		title: null,
		isOnline: null,
		isLoading: null,
		devices: [],
		serverCount: null,
		secondaryApplianceId: null,
		affectedDevices: null
	}


	private get isTable(): boolean {
		return !this.responseMessage && !this.confirmationMessageShown && !this.saveConfirmationMessageShown && !this.isMoveBackConfirmationShown;
	}

	private get isResponseMessage(): boolean {
		return this.responseMessage && !this.confirmationMessageShown && !this.saveConfirmationMessageShown && !this.isMoveBackConfirmationShown;
	}

	private get isFailoverConfirmation(): boolean {
		return !this.responseMessage &&	this.confirmationMessageShown && !this.saveConfirmationMessageShown && !this.isMoveBackConfirmationShown;
	}

	private get isSaveConfirmation(): boolean {
		return !this.responseMessage &&	!this.confirmationMessageShown && this.saveConfirmationMessageShown && !this.isMoveBackConfirmationShown;
	}

	private get isMoveBackConfirmation(): boolean {
		return !this.responseMessage &&	!this.confirmationMessageShown && !this.saveConfirmationMessageShown && this.isMoveBackConfirmationShown;
	}

	private get isFailoverEnabled(): boolean {
		return this.getFeature(["Devices", "AdvancedSetup", "ApplianceFailover"]) && (this.getPermissions.canFailOverAppliance || this.getPermissions.isSystemAdmin);
	}

	private get isDeviceLevelFailoverEnabled(): boolean {
		return this.getFeature(["Devices", "AdvancedSetup", "ApplianceFailover", "DeviceLevel"]);
	}

	private get columns(): TableHeader[]
	{
		return [
			{
				title: "Ops Link",
				key: "title",
				order: 1,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: true,
				useCustomCell: true,
				isTermLabel: true
			},
			{
				title: "Status",
				key: "isOnline",
				order: 1,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: true,
				useCustomCell: true,
				isTermLabel: true
			},
			{
				title: "Devices",
				key: "devices",
				order: 2,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: true,
				useCustomCell: true,
				isTermLabel: true
			},
			{
				title: "Failed Over Devices",
				key: "affectedDevices",
				description: "Amount of Devices that have moved to another Ops Link",
				order: 2,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: this.isDeviceLevelFailoverEnabled,
				sortable: true,
				useCustomCell: true,
				isTermLabel: true
			},
			{
				title: "Failover Ops Link: ",
				key: "secondaryApplianceId",
				order: 3,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: false,
				useCustomCell: true,
				isTermLabel: true
			}
		]
	}

	private get failedOverServerColumns(): TableHeader[]
	{
		return [
			{
				title: "Selected",
				key: "isServerSelected",
				order: 1,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: false,
				useCustomCell: true,
				isTermLabel: true
			},
			{
				title: "Title",
				key: "serverTitle",
				order: 2,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: true,
				isTermLabel: true
			},
			{
				title: "Type",
				key: "serverTypeTitle",
				order: 3,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: true,
				isTermLabel: true
			},
			{
				title: "Area",
				key: "groupTitle",
				order: 4,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: false,
				isTermLabel: true
			},
			{
				title: "Current Ops Link",
				key: "currentApplianceTitle",
				order: 5,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: false,
				isTermLabel: true
			},
			{
				title: "Primary Ops Link",
				key: "primaryApplianceTitle",
				order: 5,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: false,
				isTermLabel: true
			},
		]
	}

	private async created(): Promise<void> {
		if (!this.isFailoverEnabled) {
			return;
		}

		if (this.isDeviceLevelFailoverEnabled) {
			await this.loadFailOverCount();
			await this.pollAffectedDevices();
		}

		this.selectedOpsLinkForReset = -1;
	}

	private destroyed(): void {
		if (this.pollAffectedDevicesBadgeTimeout){
			clearTimeout(this.pollAffectedDevicesBadgeTimeout);
			this.pollAffectedDevicesBadgeTimeout = null;
		}
	}

	private async openModal(): Promise<void> {
		this.selectedOpsLinkForReset = -1;

		await this.loadAppliances();
		this.opsLinkModalShown = true;
	}

	private async pollAffectedDevices(): Promise<void> {
		if(this.pollAffectedDevicesBadgeTimeout != null) {
			await this.loadFailOverCount();
		}

		this.pollAffectedDevicesBadgeTimeout = setTimeout(this.pollAffectedDevices, 5000);
	}

	private async loadAppliances(paginatedSearchQueryParams?: PaginatedSearchQueryParams): Promise<void> {
		if (paginatedSearchQueryParams == null) {
			paginatedSearchQueryParams = this.defaultSearchParams;
		}

		this.opsLinkTableLoading = true;

		this.opsLinksList = [];

		this.opsLinkPageNumber = paginatedSearchQueryParams.page;
		this.opsLinkPageSize = paginatedSearchQueryParams.pageSize;
		this.opsLinkSearchTerm = paginatedSearchQueryParams.searchTerm?.toLowerCase();
		this.opsLinkSortDesc = paginatedSearchQueryParams.sortDesc;
		this.opsLinkSortBy = paginatedSearchQueryParams.sortBy;

		this.opsLinkCancelTokenSource = getNewCancelTokenSource(this.opsLinkCancelTokenSource, true);

		const cancellableQuery: CancellableQuery<PaginatedSearchQueryParams> = {
			params: {
				page: this.opsLinkPageNumber,
				pageSize: this.opsLinkPageSize,
				sortBy: this.opsLinkSortBy,
				sortDesc: this.opsLinkSortDesc,
				searchTerm: this.opsLinkSearchTerm
			},
			cancelTokenSource: this.opsLinkCancelTokenSource
		};

		try {
			this.opsLinksList = (await this.getAppliancesPaginated(cancellableQuery)).map(opsLink => {
				let result: OpsLinkExtended = {
					id: opsLink.applianceId,
					groupId: opsLink.groupId,
					title: opsLink.title,
					isOnline: opsLink.isOnline,
					isLoading: false,
					devices: [],
					serverCount: opsLink.serverCount,
					secondaryApplianceId: opsLink.secondaryApplianceId,
					affectedDevices: opsLink.affectedDevices
				};
				return result;
			});
		}
		catch (e) {
			this.$notify({
				type: "error",
				title: "Error loading Ops Links",
				text: `Unable to load Ops Links`
			});
			console.error(e);
		}
		finally {
			this.opsLinkTableLoading = false;
		}
	}

	private setSelectedDetails(currentRow: OpsLinkExtended): void {
		if (currentRow.secondaryApplianceId == null) {
			this.resetSelectedOpsLinks();
		}

		this.showResponseMessage = false;
		this.responseMessage = "";

		this.currentOpsLink = currentRow;

		let failover = this.opsLinksList.firstOrDefault(ol => ol.id == currentRow.secondaryApplianceId);

		if (failover != null) {
			this.secondaryOpsLink = failover;
		}
	}

	private resetSelectedOpsLinks(): void {
		this.currentOpsLink = {
			id: null,
			groupId: null,
			title: null,
			isOnline: null,
			isLoading: null,
			devices: [],
			serverCount: null,
			secondaryApplianceId: null,
			affectedDevices: null
		};

		this.secondaryOpsLink = {
			id: null,
			groupId: null,
			title: null,
			isOnline: null,
			isLoading: null,
			devices: [],
			serverCount: null,
			secondaryApplianceId: null,
			affectedDevices: null
		};
	}

	private showSaveConfirmation(currentId: number): void {
		if (currentId == this.currentOpsLink.id) {
			this.saveConfirmationMessageShown = true;
		}
	}

	private showFailoverConfirmation(currentRow: OpsLinkExtended): void {
		this.setSelectedDetails(currentRow);
		if (currentRow.id == this.currentOpsLink.id && this.secondaryOpsLink.id != null) {
			this.confirmationMessageShown = true;
		}
	}

	private async failOver(): Promise<void> {
		await this.failOverDebounce(this);
	}

	private async setSecondaryAppliance(): Promise<void> {
		await this.updateSecondaryApplianceDebounce(this);
	}

	private failOverDebounce = debounce(async (component: InstanceType<typeof OpsLinks>): Promise<void> => {
		let requestSuccess = true;
		component.loadSpinner = true;

		try
		{
			await api.serversChangeAppliance(component.currentOpsLink.id, component.secondaryOpsLink.id);
		}
		catch (ex)
		{
			requestSuccess = false;
			const message = ex.toString();
			component.responseMessage = message;
			component.showResponseMessage = true;
			component.$notify({
				type: "error",
				title: "Secondary Ops Link Request Failed",
				text: message
			})

			console.error(ex);
		}
		finally
		{
			if (requestSuccess)
			{
				const message = `"${component.currentOpsLink.title}" has been switched to "${component.secondaryOpsLink.title}".\nAlarm integrations will move to "${component.secondaryOpsLink.title}" within 60 seconds.\nSync Systems will move to "${component.secondaryOpsLink.title}" within 5 minutes.`
				component.responseMessage = message;
				component.showResponseMessage = true;

				await component.loadAppliances();
			}

			await this.refreshTables();

			component.loadSpinner = false;
		}

		component.resetSelectedOpsLinks();
		component.confirmationMessageShown = false;
	}, getDebouncePeriod());

	private updateSecondaryApplianceDebounce = debounce(async (component: InstanceType<typeof OpsLinks>): Promise<void> => {
		let requestSuccess = true;
		component.loadSpinner = true;

		try
		{
			await api.updateSecondaryAppliance(component.currentOpsLink.id, component.secondaryOpsLink.id);
		}
		catch (ex)
		{
			requestSuccess = false;
			const message = ex.toString();
			component.responseMessage = message;
			component.showResponseMessage = true;
			component.$notify({
				type: "error",
				title: "Failed to update Secondary Ops Link",
				text: message
			})

			console.error(ex);
		}
		finally
		{
			if (requestSuccess)
			{
				const message = component.secondaryOpsLink.title != null ?
					`Default Secondary Ops Link for "${component.currentOpsLink.title}" has been set to "${component.secondaryOpsLink.title}".` :
					`Default Secondary Ops Link removed for "${component.currentOpsLink.title}".`;
				component.responseMessage = message;
				component.showResponseMessage = true;

				await component.refreshTables();
			}

			component.loadSpinner = false;
		}

		component.resetSelectedOpsLinks();
		component.saveConfirmationMessageShown = false;
	}, getDebouncePeriod());

	@Watch("selectedOpsLinkForReset")
	private async getFailedOverServers(paginatedSearchQueryParams?: PaginatedSearchQueryParams): Promise<void> {
		if (!this.isFailoverEnabled) {
			return;
		}

		if (this.selectedOpsLinkForReset == null) {
			return;
		}

		if (paginatedSearchQueryParams == null) {
			paginatedSearchQueryParams = this.defaultSearchParams;
		}

		this.affectedDevicesLoading = true;

		this.setFailedOverDevices = [];

		this.affectedDevicesPageNumber = paginatedSearchQueryParams.page;
		this.affectedDevicesPageSize = paginatedSearchQueryParams.pageSize;
		this.affectedDevicesSearchTerm = paginatedSearchQueryParams.searchTerm?.toLowerCase();
		this.affectedDevicesSortDesc = paginatedSearchQueryParams.sortDesc;
		this.affectedDeviceSortBy = paginatedSearchQueryParams.sortBy;

		this.affectedDevicesCancelTokenSource = getNewCancelTokenSource(this.affectedDevicesCancelTokenSource, true);

		const cancellableQuery: CancellableQuery<PaginatedSearchQueryParams> = {
			params: {
				applianceId: this.selectedOpsLinkForReset == -1 ? null : this.selectedOpsLinkForReset,
				page: this.affectedDevicesPageNumber,
				pageSize: this.affectedDevicesPageSize,
				sortBy: this.affectedDeviceSortBy,
				sortDesc: this.affectedDevicesSortDesc,
				searchTerm: this.affectedDevicesSearchTerm
			},
			cancelTokenSource: this.affectedDevicesCancelTokenSource
		};

		try {
			await this.getFailedOverServersPaginated(cancellableQuery);

			this.configuredFailedOverDevices = this.failedOverDevicesList.map((server) => {
				return { ...server,	isServerSelected: false	}
			});
		}
		catch (e) {
			this.$notify({
				type: "error",
				title: "Error loading Devices",
				text: `Unable to load moved Devices`
			});
			console.error(e);
		}
		finally {
			this.affectedDevicesLoading = false;
		}
	}

	private opsLinkForSelect(excludeApplianceId: number): OpsLinkExtended[] {
		return this.opsLinksList.filter(ol => ol.id != excludeApplianceId);
	}

	private isButtonDisabled(applianceId: number | null): boolean {
		if (applianceId == null) {
			return true;
		}

		if (this.currentOpsLink.id == applianceId && this.currentOpsLink.secondaryApplianceId != null) {
			return false;
		}

		return true;
	}

	private get selectedServers(): FailedOverServer[] {
		return this.configuredFailedOverDevices.filter(s => s.isServerSelected);
	}

	private get serversSelected(): boolean {
		return this.selectedServers != null && this.selectedServers.length > 0;
	}

	private moveDevicesConfirmation(): void {
		this.isMoveBackConfirmationShown = true;
	}

	private async moveDevicesBack(): Promise<void> {
		this.loadSpinner = true;

		try	{
			const id = this.selectedOpsLinkForReset == -1 ? null : this.selectedOpsLinkForReset;
			await this.moveFailedOverServersForAppliance(id);
		}
		catch (e) {
			this.$notify({
				type: "error",
				title: "Error moving Devices",
				text: `Unable to move Devices`
			});
			console.error(e);
		}
		finally {
			await this.refreshTables();
			this.isMoveBackConfirmationShown = false;
			this.loadSpinner = false;
		}
	}

	private async moveSelectedDevicesBack(): Promise<void> {
		if (this.selectedServers == null || this.selectedServers.length < 1) {
			return;
		}

		this.loadSpinner = true;

		try	{
			const ids = this.selectedServers.map(server => {
				return server.serverId;
			})

			await this.moveSelectedServersBack(ids);
		}
		catch (e) {
			this.$notify({
				type: "error",
				title: "Error moving Devices",
				text: `Unable to move Devices`
			});
			console.error(e);
		}
		finally {
			await this.refreshTables();
			this.isMoveBackConfirmationShown = false;
			this.loadSpinner = false;
		}
	}

	private get affectedOpsLinkOptions(): OpsLinkExtended[] {
		let options = [
			{
				title: "All Ops Links",
				id: -1,
				serverCount: null,
				secondaryApplianceId: null,
				groupId: null,
				isOnline: false,
				devices: null,
				isLoading: false
			}
		] as OpsLinkExtended[];

		options.push(...this.affectedOpsLinks);

		return options;
	}

	private get affectedOpsLinks(): OpsLinkExtended[] {
		return this.opsLinksList.filter(ol => ol.affectedDevices != null && ol.affectedDevices > 0);
	}

	private selectAffectedDevices(primaryApplianceId: number): void {
		if (primaryApplianceId == null) {
			return;
		}

		const validOpsLinks = this.affectedOpsLinks.filter(ol => ol.id == primaryApplianceId);
		if (validOpsLinks == null || validOpsLinks.length < 1) {
			return;
		}

		this.selectedOpsLinkForReset = primaryApplianceId;
		this.tabIndex = 1;
	}

	private async refreshTables(): Promise<void> {
		this.configuredFailedOverDevices = [];

		await this.loadAppliances();

		await this.getFailedOverServers();
		this.selectedOpsLinkForReset = -1;
	}

	private getOpsLinkTitleById(id: number): string {
		if (id == null) {
			return "No Ops Link Found";
		}

		const opsLink = this.opsLinksList.firstOrDefault(ol => ol.id == id);
		return opsLink?.title != null ? opsLink.title : "No Ops Link Found";
	}

	@Emit("modalClosed")
	private async closeOpsLinksModal(): Promise<void> {
		this.opsLinkModalShown = false;
		this.resetSelectedOpsLinks();
		this.showResponseMessage = false;
		this.responseMessage = "";
		this.selectedOpsLinkForReset = null;
		this.configuredFailedOverDevices = [];
		this.opsLinkTableLoading = false;
		this.affectedDevicesLoading = false;
		this.tabIndex = 0;
	}

	private resetResponse(): void {
		this.resetSelectedOpsLinks();
		this.showResponseMessage = false;
		this.responseMessage = "";
	}
}
